import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react'
import { BoatData, School, BoatInfo, PlaybackState } from '../types'
import { translations, Language } from '../translations'

const HISTORY_SIZE = 60

interface AppContextType {
    // School & Auth
    currentSchool: School | null
    setCurrentSchool: (school: School | null) => void

    // Boats
    availableBoats: BoatInfo[]
    setAvailableBoats: (boats: BoatInfo[]) => void
    selectedBoats: string[]
    toggleBoatSelection: (boatId: string) => void

    // Live Data
    liveData: Map<string, BoatData>
    updateLiveData: (boatId: string, data: BoatData) => void
    liveDataHistory: Map<string, BoatData[]>

    // All-boats live tracking (for banner + map ghost markers)
    allBoatsLiveData: Map<string, BoatData>
    updateAllBoatsLiveData: (boatId: string, data: BoatData) => void
    switchToLive: (boatId?: string) => void

    // Playback
    playbackState: PlaybackState
    setPlaybackState: (state: Partial<PlaybackState> | ((prev: PlaybackState) => Partial<PlaybackState>)) => void

    // UI State
    sidebarOpen: boolean
    setSidebarOpen: (open: boolean) => void
    dashboardOpen: boolean
    setDashboardOpen: (open: boolean) => void
    refreshTrigger: number
    triggerRefresh: () => void
    sourceVersion: number
    bumpSource: () => void

    // I18n
    language: Language
    setLanguage: (lang: Language) => void
    t: (path: string) => string
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export function AppProvider({ children }: { children: ReactNode }) {
    const [currentSchool, setCurrentSchool] = useState<School | null>(null)
    const [availableBoats, setAvailableBoats] = useState<BoatInfo[]>([])
    const [selectedBoats, setSelectedBoats] = useState<string[]>([])
    const [liveData, setLiveData] = useState<Map<string, BoatData>>(new Map())
    const [allBoatsLiveData, setAllBoatsLiveData] = useState<Map<string, BoatData>>(new Map())
    const liveDataHistoryRef = useRef<Map<string, BoatData[]>>(new Map())
    const [liveDataHistory, setLiveDataHistory] = useState<Map<string, BoatData[]>>(new Map())
    const [sidebarOpen, setSidebarOpen] = useState(true)
    const [dashboardOpen, setDashboardOpen] = useState(true)
    const [refreshTrigger, setRefreshTrigger] = useState(0)
    const [sourceVersion, setSourceVersion] = useState(0)
    const [language, setLanguage] = useState<Language>('en')

    const [playbackState, setPlaybackStateInternal] = useState<PlaybackState>({
        isPlaying: false,
        currentIndex: 0,
        speed: 1,
        startTime: null,
        endTime: null,
        frames: []
    })

    const toggleBoatSelection = useCallback((boatId: string) => {
        setSelectedBoats(prev =>
            prev.includes(boatId)
                ? prev.filter(id => id !== boatId)
                : [...prev, boatId]
        )
    }, [])

    const updateLiveData = useCallback((boatId: string, data: BoatData) => {
        setLiveData(prev => {
            const newMap = new Map(prev)
            newMap.set(boatId, data)
            return newMap
        })
        // Append to rolling history buffer
        const histMap = liveDataHistoryRef.current
        const arr = histMap.get(boatId) || []
        arr.push(data)
        if (arr.length > HISTORY_SIZE) arr.shift()
        histMap.set(boatId, arr)
        setLiveDataHistory(new Map(histMap))
    }, [])

    const updateAllBoatsLiveData = useCallback((boatId: string, data: BoatData) => {
        setAllBoatsLiveData(prev => {
            const newMap = new Map(prev)
            newMap.set(boatId, data)
            return newMap
        })
    }, [])

    const switchToLive = useCallback((boatId?: string) => {
        // Clear playback
        setPlaybackStateInternal({
            isPlaying: false,
            currentIndex: 0,
            speed: 1,
            startTime: null,
            endTime: null,
            frames: []
        })
        // If a specific boat is requested, select it
        if (boatId) {
            setSelectedBoats(prev => prev.includes(boatId) ? prev : [...prev, boatId])
        }
    }, [])

    const setPlaybackState = useCallback((update: Partial<PlaybackState> | ((prev: PlaybackState) => Partial<PlaybackState>)) => {
        setPlaybackStateInternal(prev => {
            const partial = typeof update === 'function' ? update(prev) : update
            return { ...prev, ...partial }
        })
    }, [])

    const triggerRefresh = useCallback(() => {
        setRefreshTrigger(prev => prev + 1)
    }, [])

    // Position-source switch (Auto / Phone GPS / PPK): only the SELECTED boats need to resubscribe —
    // NOT the whole boat list and NOT the all-boats ghost listeners. Kept separate from refreshTrigger
    // so flipping source no longer reloads the list or restarts every boat's stream.
    const bumpSource = useCallback(() => {
        setSourceVersion(prev => prev + 1)
    }, [])

    const t = useCallback((path: string) => {
        const keys = path.split('.')
        let current: any = translations[language]
        for (const key of keys) {
            if (current[key] === undefined) return path
            current = current[key]
        }
        return current
    }, [language])

    return (
        <AppContext.Provider value={{
            currentSchool,
            setCurrentSchool,
            availableBoats,
            setAvailableBoats,
            selectedBoats,
            toggleBoatSelection,
            liveData,
            updateLiveData,
            liveDataHistory,
            allBoatsLiveData,
            updateAllBoatsLiveData,
            switchToLive,
            playbackState,
            setPlaybackState,
            sidebarOpen,
            setSidebarOpen,
            dashboardOpen,
            setDashboardOpen,
            refreshTrigger,
            triggerRefresh,
            sourceVersion,
            bumpSource,
            language,
            setLanguage,
            t
        }}>
            {children}
        </AppContext.Provider>
    )
}

export function useApp() {
    const context = useContext(AppContext)
    if (!context) {
        throw new Error('useApp must be used within AppProvider')
    }
    return context
}
